import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { Provider, useDispatch } from 'react-redux';
import store from '@/redux/store';
import AppRoutes from '@/router';
import { authActions } from '@/redux/auth/authSlice';
import { queryClient } from '@/query/queryClient';
import { clearLegacyRefreshStorage } from '@/services/auth';
import { useAccountsSessionSync } from '@/hooks/auth/useAccountsSessionSync';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const SessionHydrate = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    clearLegacyRefreshStorage();
    dispatch(authActions.hydrateFromSession());
  }, [dispatch]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'accessToken' || e.key === 'user') {
        dispatch(authActions.hydrateFromSession());
      }
    };
    window.addEventListener('storage', onStorage);
    const onFocus = () => dispatch(authActions.hydrateFromSession());
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, [dispatch]);

  return children;
};

const AccountsSessionSync = () => {
  useAccountsSessionSync();
  return null;
};

const AppRouting = () => (
  <>
    <ScrollToTop />
    <SessionHydrate>
      <AccountsSessionSync />
      <AppRoutes />
    </SessionHydrate>
  </>
);

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRouting />
      </BrowserRouter>
    </QueryClientProvider>
  </Provider>
);

export default App;
